/**
 * Polish bank account number (NRB / IBAN) utilities.
 *
 * Rules:
 *  - 26 digits optionally preceded by "PL" (case-insensitive) and/or separated by spaces.
 *  - Normalised storage format: "PL" + 26 digits, no spaces.
 *  - Display format:  "PL61 1090 1014 0000 0712 1981 2874"  (groups of 4 after the 2-digit check number).
 */

/** Return the raw 26-digit string, stripping spaces and optional "PL" prefix. */
export function extractDigits(raw: string): string {
  const stripped = raw.replace(/\s+/g, "").toUpperCase();
  return stripped.startsWith("PL") ? stripped.slice(2) : stripped;
}

/** True if `raw` contains exactly 26 digits (with optional PL prefix and spaces). */
export function isValidAccountNumber(raw: string): boolean {
  return /^\d{26}$/.test(extractDigits(raw));
}

/**
 * Format to "PL61 1090 1014 0000 0712 1981 2874".
 * Returns the original string unchanged if it doesn't contain valid 26 digits.
 */
export function formatAccountNumber(raw: string): string {
  const digits = extractDigits(raw);
  if (!/^\d{26}$/.test(digits)) return raw;
  return (
    "PL" +
    digits.slice(0, 2) + " " +
    digits.slice(2, 6) + " " +
    digits.slice(6, 10) + " " +
    digits.slice(10, 14) + " " +
    digits.slice(14, 18) + " " +
    digits.slice(18, 22) + " " +
    digits.slice(22, 26)
  );
}
